// Necessary for typing in actions and getters.
/* eslint-disable @typescript-eslint/no-use-before-define, no-use-before-define */

import { ActionContext } from 'vuex';
import { defineModule } from 'direct-vuex';
import { useAuth } from '@/services/auth';
import { killFirestoreListeners, initSyncFirestoreWithVuexStore } from '@/utils';
import { moduleActionContext, moduleGetterContext } from '@/store';
import { User } from '@firebase/auth-types';
import { useGapi } from '@/services';

export interface AuthState {
  firebaseUser: User | null;
  accessToken: string | undefined;
  refreshToken: string | undefined;
}

export const authModule = defineModule({
  namespaced: true,
  state: (): AuthState => ({
    firebaseUser: null,
    accessToken: undefined,
    refreshToken: undefined
  }),
  getters: {
    isSignedIn: (...args): boolean => {
      const { state } = getterContext(args);
      if (state.accessToken === undefined || state.accessToken === null) return false;
      if (state.refreshToken === undefined || state.refreshToken === null) return false;
      return state.firebaseUser !== null;
    },
    accessToken: (...args): string | undefined => {
      const { state } = getterContext(args);
      return state.accessToken;
    },
    refreshToken: (...args): string | undefined => {
      const { state } = getterContext(args);
      return state.refreshToken;
    }
  },
  mutations: {
    SET_FIREBASE_USER: (state, firebaseUser: User): void => {
      state.firebaseUser = firebaseUser;
    },
    CLEAR_FIREBASE_USER: (state): void => {
      state.firebaseUser = null;
    },
    SET_ACCESS_TOKEN: (state, accessToken: string | undefined): void => {
      state.accessToken = accessToken;
    },
    CLEAR_ACCESS_TOKEN: (state): void => {
      state.accessToken = undefined;
    },
    SET_REFRESH_TOKEN: (state, refreshToken: string | undefined): void => {
      state.refreshToken = refreshToken;
    },
    CLEAR_REFRESH_TOKEN: (state): void => {
      state.refreshToken = undefined;
    }
  },
  actions: {
    setAccessToken: (context, accessToken: string): void => {
      const { commit } = actionContext(context);
      commit.SET_ACCESS_TOKEN(accessToken);
    },
    signIn: async (context): Promise<void> => {
      const { state, commit } = actionContext(context);
      const { signInToGoogle } = useGapi();

      if (!state.firebaseUser) {
        const { authInfo, refreshToken }: any = await signInToGoogle();
        const { user, credential } = authInfo;

        if (user && credential) {
          const { accessToken } = credential;
          commit.SET_ACCESS_TOKEN(accessToken);
          commit.SET_REFRESH_TOKEN(refreshToken);
          commit.SET_FIREBASE_USER(user);
        }
      }

      await initSyncFirestoreWithVuexStore();
    },
    signOut: async (context): Promise<void> => {
      const { commit, rootCommit } = actionContext(context);
      const { signOut: signOutFirebase } = useAuth();

      await killFirestoreListeners();
      rootCommit.groups.CLEAR_GROUP_ID();
      rootCommit.groups.CLEAR_LINK_ID();
      commit.CLEAR_ACCESS_TOKEN();
      commit.CLEAR_REFRESH_TOKEN();
      commit.CLEAR_FIREBASE_USER();
      await signOutFirebase();
    }
  }
});

const actionContext = (context: ActionContext<unknown, unknown>) => moduleActionContext(context, authModule);
const getterContext = (args: [unknown, unknown, unknown, unknown]) => moduleGetterContext(args, authModule);
